<template>
    <div
        class="flex overflow-hidden flex-col items-center justify-center h-screen px-0 mx-auto lg:py-0 w-full bg-cover bg-center  bg-no-repeat bg-[url('/assets/static/pn-vojens-620x280.jpg')]">
       
       <div v-motion="{
            initial: {
                opacity: 0.5
            },
            enter: {
                opacity: 1
            },
        }" :delay="200"
            class="w-full h-full flex  justify-center items-center backdrop-blur-md bg-teal-950/80 overflow-hidden ">
            <div v-motion="{
                initial: { y: 700, opacity: 0 },
                enter: { y: 0, opacity: 1, transition: { delay: 300 }, }
            }" class="w-full bg-card-primary rounded-lg shadow md:mt-0 sm:max-w-md xl:p-0 max-md:mx-4">
                <div class="p-6 sm:p-8">
                    <h1 v-motion-slide-visible-bottom
                        class="text-xl font-bold leading-tight tracking-tight text-black-100 md:text-2xl">
                        Sign in to your account
                    </h1>

                    <div class="space-y-4 md:space-y-6 relative">
                        <div v-motion="{
                            initial: { y: 100, opacity: 0 },
                            enter: {
                                y: 0,
                                opacity: 1,
                                transition: {
                                    delay: 500,
                                },
                            }
                        }">
                            <label for="email" class="mb-2 text-sm font-medium text-text-primary ">Your admin/localadmin username or email</label>
                            <input v-model="email" v-on:keyup.enter="loginUser" type="email" name="email" id="email"
                                class="border border-gray-300 text-text-primary sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5  "
                                placeholder="ex. localadminDK">
                        </div>
                        <div class="relative z-0" v-motion="{
                            initial: { y: 100, opacity: 0 },
                            enter: {
                                y: 0,
                                opacity: 1,
                                transition: {
                                    delay: 600,
                                },
                            }
                        }">

                            <label for="password" class="mb-2 text-sm font-medium text-text-primary ">Password</label>
                            <input @keyup.enter="loginUser()" v-model="pass" type="password" name="password"
                                id="password" placeholder="••••••••"
                                class="border border-gray-300 text-text-primary sm:text-sm rounded-lg focus:ring-primary-600 focus:border-primary-600 block w-full p-2.5"
                                required="">
                        </div>

                        <div class="flex items-center gap-2">
                            <input
                                id="remember-me"
                                v-model="rememberMe"
                                type="checkbox"
                                class="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary-600"
                            >
                            <label for="remember-me" class="text-sm text-text-primary">Stay signed in for 30 days</label>
                        </div>

                        <button v-if="!loading" :disabled="email == '' || pass == ''" @click="loginUser"
                            v-on:keyup.enter="loginUser"
                            class="cursor-pointer w-full text-white bg-primary hover:bg-primary-dark disabled:opacity-50 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center ">
                            <span>Sign in</span>
                        </button>

                        <div v-if="loading">
                            <UProgress animation="carousel" />
                        </div>

                    </div>
                    <p v-motion="{
                        initial: { y: 0, opacity: 0 },
                        enter: {
                            y: 0,
                            opacity: 1,
                            transition: {
                                delay: 0,
                            },
                        }
                    }" class="font-bold text-center text-red-400 my-2" v-if="errorMessage && !loading">{{ errorMessage }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">

import type { AccessLevel } from '~/types/api/user/accesslevel'
import { API } from '~/utils/api'
const email: Ref<string> = ref('')
const pass: Ref<string> = ref('')
const errorMessage = ref('')
const route = useRoute();
const loading: Ref<boolean> = ref(false);
const rememberMe = ref(false);
const config = useRuntimeConfig();
const userStore = useUserStore()
const {user} = storeToRefs(userStore)
const api = new API()
const selectedClient = ref('')

definePageMeta({
    layout: false
})

function clearDashboardAuthCookies() {
    const expire = { maxAge: -1, path: '/' as const }
    useCookie('JSESSIONID', expire).value = null
    useCookie('USERINFO', expire).value = null
    useCookie('user', expire).value = null
}

onMounted(async () => {
    const jsessionid = useCookie('JSESSIONID')
    if (!jsessionid.value) return

    const requestFetch = useRequestFetch()
    try {
        const accessLevel = await requestFetch<AccessLevel[]>('/api/user/accesslevel')
        if (accessLevel && accessLevel.length > 0) {
            const redirectUri = route.query?.redirect_uri as string | undefined
            if (redirectUri && !redirectUri.includes('/login')) {
                await navigateTo(decodeURIComponent(redirectUri))
                return
            }
            await navigateTo('/dashboard')
            return
        }

        clearDashboardAuthCookies()
    } catch (err: any) {
        if (err.statusCode == 403 || err.statusCode == 401) {
            clearDashboardAuthCookies()
        }
    }
})

// Define an asynchronous function to handle user login
const loginUser = async () => {
    // Set loading state to true
    loading.value = true;
    errorMessage.value = '';
    try {
        // Call the logUserIn method from the API and pass the email, password, and redirect URL
        const res = await api.user().logUserIn(selectedClient.value, email.value, pass.value, route.query?.redirect_uri ? decodeURIComponent(route.query?.redirect_uri as string) : undefined, rememberMe.value);
        // Check if the response status is not 200
        if (Number(res.status) !== 200) {
            // Set the error message to the response status text
            errorMessage.value = res.statusText
            loading.value = false;
            return;
        }
        if(res.user) {
            user.value = res.user
        }
        if (res?.redirect) {
            console.log('Login.vue: Redirecting to redirect', decodeURIComponent(res.redirect))
            navigateTo(decodeURIComponent(res.redirect))
        }

    } catch (err: any) {
        // Set loading state to false in case of an error
        loading.value = false;
        // Set the error message to the error message
        errorMessage.value = err
    }
}



</script>
